<p-card styleClass="h-full">
  <div class="flex align-items-center gap-2 mb-2">
    <i class="pi pi-map-marker text-xl"></i>
    <h2 class="m-0">Test de routabilité</h2>
  </div>
  <p class="text-sm text-color-secondary mt-0 mb-4 line-height-3">
    Vérifie si des coordonnées sont accessibles par le moteur de routage
    (Valhalla). Un point est jugé <strong>non routable</strong> s'il est hors
    couverture, trop loin d'une route, ou snappé sur un tronçon mal connecté
    (atteignabilité &lt; seuil) — c'est ce qui provoque les erreurs
    <code>Unfound route(s) to location</code> côté VROOM.
  </p>

  <div class="grid">
    <!-- Saisie -->
    <div class="col-12 lg:col-5">
      <label class="block font-semibold text-sm mb-2">
        Coordonnées (une par ligne)
      </label>
      <textarea
        [(ngModel)]="rawInput"
        rows="8"
        class="w-full p-2 border-round border-1 surface-border font-mono text-sm"
        placeholder="48.8566, 2.3522&#10;49.560427, -1.826790"
      ></textarea>

      <div class="flex flex-column gap-2 mt-3">
        <span class="font-semibold text-sm">Ordre des valeurs</span>
        <div class="flex flex-wrap gap-3">
          <label class="flex align-items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="inputOrder"
              value="lat_lng"
              [(ngModel)]="inputOrder"
            />
            <span class="text-sm">latitude, longitude (Google Maps)</span>
          </label>
          <label class="flex align-items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="inputOrder"
              value="lng_lat"
              [(ngModel)]="inputOrder"
            />
            <span class="text-sm">longitude, latitude (format VROOM)</span>
          </label>
        </div>
      </div>

      <div class="flex align-items-center gap-2 mt-3">
        <label
          class="font-semibold text-sm"
          pTooltip="Nombre minimum de nœuds connectés requis. Doit refléter la config Valhalla (minimum_reachability, 20 par défaut)."
        >
          Seuil d'atteignabilité
        </label>
        <p-inputNumber
          [(ngModel)]="minReachability"
          [min]="0"
          [max]="100"
          [showButtons]="true"
          inputStyleClass="w-4rem"
        />
      </div>

      <div class="flex flex-wrap gap-2 mt-4">
        <p-button
          label="Tester"
          icon="pi pi-play"
          [loading]="loading"
          (onClick)="test()"
        />
        <p-button
          label="Exemple"
          icon="pi pi-bolt"
          severity="secondary"
          [outlined]="true"
          (onClick)="fillExample()"
        />
        <p-button
          label="Effacer"
          icon="pi pi-times"
          severity="secondary"
          [text]="true"
          (onClick)="clear()"
        />
      </div>

      @if (parseErrors.length > 0) {
        <div class="mt-3 text-sm text-orange-600">
          @for (err of parseErrors; track err) {
            <div><i class="pi pi-exclamation-triangle mr-1"></i>{{ err }}</div>
          }
        </div>
      }
    </div>

    <!-- Carte -->
    <div class="col-12 lg:col-7">
      @if (markers.length > 0) {
        <div class="border-round overflow-hidden" style="height: 400px">
          <app-map-with-markers
            [markers]="markers"
            [focusCoordinates]="focusCoordinates"
          />
        </div>
      } @else {
        <div
          class="flex align-items-center justify-content-center border-round border-1 border-dashed surface-border text-color-secondary"
          style="height: 400px"
        >
          <span class="text-sm">La carte s'affichera après un test.</span>
        </div>
      }
    </div>
  </div>

  <!-- Résultats -->
  @if (loading) {
    <div class="flex justify-content-center align-items-center py-6">
      <p-progressSpinner
        strokeWidth="4"
        [style]="{ width: '48px', height: '48px' }"
      />
    </div>
  } @else if (hasTested && results.length > 0) {
    <div class="flex align-items-center gap-3 mt-4 mb-3">
      <p-tag
        severity="success"
        [value]="routableCount + ' routable(s)'"
        icon="pi pi-check"
      />
      <p-tag
        severity="danger"
        [value]="unroutableCount + ' non routable(s)'"
        icon="pi pi-times"
      />
    </div>

    <div class="overflow-x-auto">
      <table class="w-full text-sm border-collapse routability-table">
        <thead>
          <tr class="text-left surface-100">
            <th class="p-2">Statut</th>
            <th class="p-2">Latitude</th>
            <th class="p-2">Longitude</th>
            <th class="p-2">Atteignabilité</th>
            <th class="p-2">Snap (m)</th>
            <th class="p-2">Voie</th>
            <th class="p-2">Détail</th>
          </tr>
        </thead>
        <tbody>
          @for (r of results; track $index) {
            <tr class="border-bottom-1 surface-border">
              <td class="p-2">
                @if (r.routable) {
                  <p-tag severity="success" value="OK" icon="pi pi-check" />
                } @else {
                  <p-tag severity="danger" value="KO" icon="pi pi-times" />
                }
              </td>
              <td class="p-2 font-mono">{{ r.latitude }}</td>
              <td class="p-2 font-mono">{{ r.longitude }}</td>
              <td class="p-2">
                @if (r.reachability !== null) {
                  <span
                    [class.text-red-600]="!r.routable"
                    [class.text-green-700]="r.routable"
                  >
                    {{ r.reachability }} / {{ r.min_reachability }}
                  </span>
                } @else {
                  <span class="text-color-secondary">—</span>
                }
              </td>
              <td class="p-2">
                {{ r.snap_distance_m !== null ? r.snap_distance_m : '—' }}
              </td>
              <td class="p-2">{{ r.street || '—' }}</td>
              <td class="p-2 text-color-secondary">{{ r.reason }}</td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  } @else if (hasTested) {
    <div
      class="flex flex-column align-items-center py-6 gap-2 text-color-secondary mt-4"
    >
      <i class="pi pi-info-circle text-3xl"></i>
      <p class="m-0">Aucun résultat.</p>
    </div>
  }
</p-card>
